import {
  ProfileSetupData,
  ProfileEditorData,
  ProfileAnalytics,
  VisibilitySettings,
  DiscoverySettings,
  BasicInfo,
  ProfilePhoto,
  Bio,
  Interest,
  Preferences
} from '../../components/Profile/types';
import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';
import { OnboardingStepId } from '../../types/onboarding';

/** Per-type profile media capacity for an account, resolved from its tier. */
export interface MediaAllowanceEntry {
  used: number;
  /** null when the tier has no ceiling. */
  max: number | null;
  unlimited: boolean;
  remaining: number | null;
  full: boolean;
  /** Already above the ceiling — e.g. after a subscription lapsed. */
  over: boolean;
}

export interface MediaAllowance {
  tier: string;
  images: MediaAllowanceEntry;
  videos: MediaAllowanceEntry;
}

const API_BASE = `${config.api.baseUrl}/users`;

export interface CompletenessItem {
  key: string;
  label: string;
  why: string;
  weight: number;
  /** OnboardingStepId of the step that collects this field. */
  step: OnboardingStepId;
}

export interface ProfileCompleteness {
  score: number;
  isComplete: boolean;
  completed: string[];
  missing: CompletenessItem[];
}

// Local-time date <-> string helpers — deliberately avoid new Date(string) /
// Date.toJSON() (which JSON.stringify calls implicitly) here, since both
// round-trip through UTC and can shift a date-only value by one day
// depending on the browser's timezone offset.
function parseLocalISODate(dateString?: string | null): Date {
  if (!dateString) return new Date();
  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
}

function toLocalISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Maps this domain's flat DB rows into the frontend's nested shapes
function toBasicInfo(row: any): BasicInfo {
  return {
    firstName: row.first_name ?? '',
    lastName: row.last_name ?? '',
    dateOfBirth: parseLocalISODate(row.date_of_birth),
    gender: row.gender ?? 'other',
    location: {
      city: row.city ?? '',
      state: row.state ?? '',
      country: row.country ?? '',
      coordinates: row.latitude && row.longitude ? { lat: row.latitude, lng: row.longitude } : undefined,
    },
    occupation: row.occupation ?? '',
    education: row.education ?? '',
    height: row.height ?? undefined,
    bodyType: row.body_type ?? undefined,
    relationshipStatus: row.relationship_status ?? undefined,
    lookingFor: row.looking_for ?? undefined,
    drinking: row.drinking ?? undefined,
    smoking: row.smoking ?? undefined,
    exercise: row.exercise ?? undefined,
    religion: row.religion ?? undefined,
    politicalViews: row.political_views ?? undefined,
    languages: row.languages ?? undefined,
    pets: row.pets ?? undefined,
    kids: row.kids ?? undefined,
    zodiac: row.zodiac ?? undefined,
  };
}

function toBio(row: any): Bio {
  const content = row.bio ?? '';
  return { content, characterCount: content.length, maxCharacters: 500, lastUpdated: new Date(row.updated_at ?? Date.now()) };
}

function toProfilePhoto(row: any): ProfilePhoto {
  return {
    id: row.id,
    url: row.photo_url,
    order: row.display_order ?? 0,
    isMain: !!row.is_main,
    type: row.media_type ?? 'image',
    thumbnail: row.thumbnail_url ?? undefined,
    duration: row.duration_seconds ?? undefined,
    uploadedAt: new Date(row.created_at),
    isValidated: row.validation_status === 'approved',
    validationStatus: row.validation_status,
    rejectionReason: row.rejection_reason ?? undefined,
  };
}

function toPreferences(row: any): Preferences {
  return {
    ageRange: { min: row.min_age ?? 18, max: row.max_age ?? 99 },
    distance: row.max_distance ?? 50,
    genderPreference: row.gender_preference ?? [],
    interestedIn: row.interested_in ?? [],
    dealBreakers: row.deal_breakers ?? [],
  };
}

function basicInfoToDbUpdates(basicInfo: Partial<BasicInfo>): Record<string, unknown> {
  return {
    firstName: basicInfo.firstName,
    lastName: basicInfo.lastName,
    dateOfBirth: basicInfo.dateOfBirth ? toLocalISODate(basicInfo.dateOfBirth) : undefined,
    gender: basicInfo.gender,
    occupation: basicInfo.occupation,
    education: basicInfo.education,
    height: basicInfo.height,
    bodyType: basicInfo.bodyType,
    relationshipStatus: basicInfo.relationshipStatus,
    lookingFor: basicInfo.lookingFor,
    drinking: basicInfo.drinking,
    smoking: basicInfo.smoking,
    exercise: basicInfo.exercise,
    religion: basicInfo.religion,
    politicalViews: basicInfo.politicalViews,
    languages: basicInfo.languages,
    pets: basicInfo.pets,
    kids: basicInfo.kids,
    zodiac: basicInfo.zodiac,
    city: basicInfo.location?.city,
    state: basicInfo.location?.state,
    country: basicInfo.location?.country,
    // Coordinates when the browser gave them to us. Without these the whole
    // distance side of the product is dead: the haversine ranking in the
    // Matches, Nearby and Explore feeds and the max_distance preference all
    // read latitude/longitude, and nothing used to write them. When they are
    // absent the backend geocodes the city instead.
    latitude: basicInfo.location?.coordinates?.lat,
    longitude: basicInfo.location?.coordinates?.lng,
  };
}

export interface NotificationPreferences {
  /** Someone commented on your post, or replied to you. */
  communityReplies: boolean;
  /** A conversation you joined has moved on. */
  communityThreads: boolean;
  /** The daily "new in your chat rooms" summary. */
  communityDigest: boolean;
  /** Your post reached 5, 10 or 25 upvotes. */
  communityMilestones: boolean;
  /** A post you saved has new comments. */
  communitySaved: boolean;
}

class ProfileService {
  private getAuthToken(): string {
    return authInterceptor.getAccessToken() || '';
  }

  private headers(json = true): HeadersInit {
    const h: HeadersInit = { 'Authorization': `Bearer ${this.getAuthToken()}` };
    if (json) h['Content-Type'] = 'application/json';
    return h;
  }

  // Profile Setup / Editor Methods

  async createProfile(profileData: ProfileSetupData): Promise<ProfileSetupData> {
    await this.updateProfileFields(basicInfoToDbUpdates(profileData.basicInfo));
    if (profileData.interests?.length) {
      await this.setUserInterests(profileData.interests.map(i => i.id));
    }
    if (profileData.preferences) {
      await this.updateDiscoverySettings('', profileData.preferences as unknown as DiscoverySettings);
    }
    return { ...profileData, isComplete: true, verificationStatus: 'pending' };
  }

  private async updateProfileFields(updates: Record<string, unknown>): Promise<any> {
    const response = await fetch(`${API_BASE}/profile`, {
      method: 'PUT',
      headers: this.headers(),
      body: JSON.stringify(updates),
    });
    if (!response.ok) throw new Error(`Failed to update profile: ${response.statusText}`);
    const { data } = await response.json();
    return data;
  }

  async getCommunityGuidelinesAccepted(): Promise<boolean> {
    const response = await fetch(`${API_BASE}/profile`, { headers: this.headers(false) });
    if (!response.ok) throw new Error(`Failed to get profile: ${response.statusText}`);
    const { data } = await response.json();
    return !!data.community_guidelines_accepted;
  }

  async acceptCommunityGuidelines(): Promise<void> {
    await this.updateProfileFields({ communityGuidelinesAccepted: true });
  }

  async getOnboardingCompleted(): Promise<boolean> {
    const response = await fetch(`${API_BASE}/profile`, { headers: this.headers(false) });
    if (!response.ok) throw new Error(`Failed to get profile: ${response.statusText}`);
    const { data } = await response.json();
    return !!data.onboarding_completed;
  }

  async completeOnboarding(): Promise<void> {
    await this.updateProfileFields({ onboardingCompleted: true });
  }

  // How much of the onboarding data the Matches algorithm scores on is
  // actually filled in. Weighted by each field's influence on ranking, not by
  // field count — see ProfileService.getCompleteness on the backend.
  async getCompleteness(): Promise<ProfileCompleteness> {
    const response = await fetch(`${API_BASE}/profile/completeness`, { headers: this.headers(false) });
    if (!response.ok) throw new Error(`Failed to get profile completeness: ${response.statusText}`);
    const { data } = await response.json();
    return {
      score: data.score ?? 0,
      isComplete: !!data.isComplete,
      completed: data.completed ?? [],
      missing: data.missing ?? [],
    };
  }

  // Theme preference — persisted server-side so it follows the user across
  // devices (localStorage is just a fast local cache on top of this).
  async getThemePreference(): Promise<'light' | 'dark' | null> {
    const response = await fetch(`${API_BASE}/profile`, { headers: this.headers(false) });
    if (!response.ok) throw new Error(`Failed to get profile: ${response.statusText}`);
    const { data } = await response.json();
    return (data.theme_preference as 'light' | 'dark') || null;
  }

  async setThemePreference(themePreference: 'light' | 'dark'): Promise<void> {
    await this.updateProfileFields({ themePreference });
  }

  async getProfile(userId?: string): Promise<ProfileEditorData> {
    // Photos and interests are per-user: when viewing someone else's profile we
    // must ask for *their* rows, not the caller's. Preferences stay private, so
    // another user's profile falls back to defaults.
    const suffix = userId ? `/${userId}` : '';

    // The profile row is required; photos/interests/preferences are enrichment,
    // so a failure there degrades to empty rather than failing the whole load.
    // (A CORS-less error response from the backend rejects fetch outright, which
    // would otherwise turn one missing endpoint into "profile won't load".)
    const optional = async (url: string) => {
      try {
        const res = await fetch(url, { headers: this.headers(false) });
        if (!res.ok) return null;
        const { data } = await res.json();
        return data;
      } catch {
        return null;
      }
    };

    const [profileRes, photoRows, interestRows, prefsRow] = await Promise.all([
      fetch(`${API_BASE}/profile${suffix}`, { headers: this.headers(false) }),
      optional(`${API_BASE}/photos${suffix}`),
      optional(`${API_BASE}/interests${suffix}`),
      userId ? Promise.resolve(null) : optional(`${API_BASE}/preferences`),
    ]);

    if (!profileRes.ok) throw new Error(`Failed to get profile: ${profileRes.statusText}`);

    const { data: profileRow } = await profileRes.json();

    return {
      basicInfo: toBasicInfo(profileRow),
      photos: (photoRows ?? []).map(toProfilePhoto),
      bio: toBio(profileRow),
      interests: (interestRows ?? []) as Interest[],
      preferences: toPreferences(prefsRow ?? {}),
      // Derived server-side from completed game sessions, so it is present on
      // anyone's profile — a badge nobody else can see is not a badge.
      badges: Array.isArray(profileRow?.badges) ? profileRow.badges : [],
      lastUpdated: new Date(),
      version: 1,
    };
  }

  async updateProfile(_userId: string, profileData: Partial<ProfileEditorData>): Promise<ProfileEditorData> {
    const updates: Record<string, unknown> = {};
    if (profileData.basicInfo) Object.assign(updates, basicInfoToDbUpdates(profileData.basicInfo));
    if (profileData.bio) updates.bio = profileData.bio.content;

    if (Object.keys(updates).length > 0) {
      await this.updateProfileFields(updates);
    }
    if (profileData.interests) {
      await this.setUserInterests(profileData.interests.map(i => i.id));
    }

    return this.getProfile();
  }

  async deleteProfile(_userId: string): Promise<void> {
    throw new Error('Account deletion is not yet implemented');
  }

  // Photo Management Methods

  /**
   * How much profile media this account may hold, per type, and how much it
   * already holds. Resolved from the subscription server-side, so the editor
   * never has to guess at the tier.
   */
  async getMediaAllowance(): Promise<MediaAllowance | null> {
    try {
      const response = await fetch(`${API_BASE}/photos/allowance`, {
        method: 'GET',
        headers: this.headers(false),
      });
      if (!response.ok) return null;
      const { data } = await response.json();
      return data ?? null;
    } catch (error) {
      // Null, not a default: the caller keeps its conservative starting value
      // rather than being told a capacity that could be wrong either way.
      console.error('Error loading media allowance:', error);
      return null;
    }
  }

  async addPhoto(
    photoUrl: string,
    mediaType: 'image' | 'video' = 'image',
    thumbnailUrl?: string,
    durationSeconds?: number
  ): Promise<ProfilePhoto> {
    const response = await fetch(`${API_BASE}/photos`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ photoUrl, mediaType, thumbnailUrl, durationSeconds }),
    });
    if (!response.ok) {
      // The backend's message rather than the status text: hitting the plan's
      // media limit needs to read as that, not as 'Failed to add photo:
      // Forbidden'.
      let message = `Failed to add photo: ${response.statusText}`;
      let code: string | undefined;
      try {
        const failure = await response.json();
        message = failure?.message || message;
        code = typeof failure?.error === 'string' ? failure.error : undefined;
      } catch {
        /* non-JSON error body */
      }
      const error = new Error(message) as Error & { code?: string; status?: number };
      error.code = code;
      error.status = response.status;
      throw error;
    }
    const { data } = await response.json();
    return toProfilePhoto(data);
  }

  /** @deprecated Real file upload to storage isn't wired up yet — pass an already-hosted URL to addPhoto() instead. */
  async uploadPhoto(_userId: string, file: File): Promise<ProfilePhoto> {
    throw new Error(`uploadPhoto(${file.name}) requires Supabase Storage integration, which is not yet built. Use addPhoto(photoUrl) with an already-hosted URL.`);
  }

  async deletePhoto(_userId: string, photoId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/photos/${photoId}`, {
      method: 'DELETE',
      headers: this.headers(false),
    });
    if (!response.ok) throw new Error(`Failed to delete photo: ${response.statusText}`);
  }

  async reorderPhotos(_userId: string, photoIds: string[]): Promise<ProfilePhoto[]> {
    const response = await fetch(`${API_BASE}/photos/reorder`, {
      method: 'PUT',
      headers: this.headers(),
      body: JSON.stringify({ photoIds }),
    });
    if (!response.ok) throw new Error(`Failed to reorder photos: ${response.statusText}`);
    const { data } = await response.json();
    return (data ?? []).map(toProfilePhoto);
  }

  async setMainPhoto(_userId: string, photoId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/photos/${photoId}/main`, {
      method: 'PUT',
      headers: this.headers(false),
    });
    if (!response.ok) throw new Error(`Failed to set main photo: ${response.statusText}`);
  }

  // Interests Methods

  async getInterestsCatalog(): Promise<Interest[]> {
    const response = await fetch(`${API_BASE}/interests/catalog`, { headers: this.headers(false) });
    if (!response.ok) throw new Error(`Failed to get interests catalog: ${response.statusText}`);
    const { data } = await response.json();
    return data;
  }

  async setUserInterests(interestIds: string[]): Promise<Interest[]> {
    const response = await fetch(`${API_BASE}/interests`, {
      method: 'PUT',
      headers: this.headers(),
      body: JSON.stringify({ interestIds }),
    });
    if (!response.ok) throw new Error(`Failed to update interests: ${response.statusText}`);
    const { data } = await response.json();
    return data;
  }

  // Settings Methods — both visibility and discovery live on the same
  // user_preferences row, so these read/write a shared endpoint under the hood.

  async getVisibilitySettings(_userId: string): Promise<VisibilitySettings> {
    const response = await fetch(`${API_BASE}/preferences`, { headers: this.headers(false) });
    if (!response.ok) throw new Error(`Failed to get visibility settings: ${response.statusText}`);
    const { data } = await response.json();
    return {
      profileVisibility: data.profile_visibility,
      showMeToEveryone: data.show_me_to_everyone,
      showDistance: data.show_distance,
      showAge: data.show_age,
    } as unknown as VisibilitySettings;
  }

  async updateVisibilitySettings(_userId: string, settings: VisibilitySettings): Promise<VisibilitySettings> {
    const response = await fetch(`${API_BASE}/preferences`, {
      method: 'PUT',
      headers: this.headers(),
      body: JSON.stringify(settings),
    });
    if (!response.ok) throw new Error(`Failed to update visibility settings: ${response.statusText}`);
    return settings;
  }

  /**
   * Account controls. Each re-checks the current password server-side; the
   * password is sent for that check and never stored.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await fetch(`${API_BASE}/password`, {
      method: 'PUT',
      headers: this.headers(),
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Could not change your password');
    }
  }

  async changeEmail(currentPassword: string, newEmail: string): Promise<void> {
    const response = await fetch(`${API_BASE}/email`, {
      method: 'PUT',
      headers: this.headers(),
      body: JSON.stringify({ currentPassword, newEmail }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Could not change your email');
    }
  }

  /** Permanent. The server cascades every row that references this account. */
  async deleteAccount(currentPassword: string): Promise<void> {
    const response = await fetch(`${API_BASE}/account`, {
      method: 'DELETE',
      headers: this.headers(),
      body: JSON.stringify({ currentPassword }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Could not delete your account');
    }
  }

  /** Which chat room notifications this user wants. Absent row = all on. */
  async getNotificationPreferences(): Promise<NotificationPreferences> {
    const response = await fetch(`${API_BASE}/notification-preferences`, { headers: this.headers(false) });
    if (!response.ok) throw new Error(`Failed to get notification preferences: ${response.statusText}`);
    const { data } = await response.json();
    return {
      communityReplies: data?.communityReplies ?? true,
      communityThreads: data?.communityThreads ?? true,
      communityDigest: data?.communityDigest ?? true,
      communityMilestones: data?.communityMilestones ?? true,
      communitySaved: data?.communitySaved ?? true,
    };
  }

  async updateNotificationPreferences(
    updates: Partial<NotificationPreferences>,
  ): Promise<NotificationPreferences> {
    const response = await fetch(`${API_BASE}/notification-preferences`, {
      method: 'PUT',
      headers: this.headers(),
      body: JSON.stringify(updates),
    });
    if (!response.ok) throw new Error(`Failed to update notification preferences: ${response.statusText}`);
    const { data } = await response.json();
    return data;
  }

  async getDiscoverySettings(_userId: string): Promise<DiscoverySettings> {
    const response = await fetch(`${API_BASE}/preferences`, { headers: this.headers(false) });
    if (!response.ok) throw new Error(`Failed to get discovery settings: ${response.statusText}`);
    const { data } = await response.json();
    return {
      ageRange: { min: data.min_age ?? 18, max: data.max_age ?? 99 },
      distance: data.max_distance ?? 50,
      genderPreference: data.gender_preference ?? [],
      interestedIn: data.interested_in ?? [],
      dealBreakers: data.deal_breakers ?? [],
    };
  }

  async updateDiscoverySettings(_userId: string, settings: DiscoverySettings): Promise<DiscoverySettings> {
    const response = await fetch(`${API_BASE}/preferences`, {
      method: 'PUT',
      headers: this.headers(),
      body: JSON.stringify({
        minAge: settings.ageRange?.min,
        maxAge: settings.ageRange?.max,
        maxDistance: settings.distance,
        genderPreference: settings.genderPreference,
        interestedIn: settings.interestedIn,
        dealBreakers: settings.dealBreakers,
      }),
    });
    if (!response.ok) throw new Error(`Failed to update discovery settings: ${response.statusText}`);
    return settings;
  }

  // Analytics Methods — served by Dashboard's profileViewsService (direct
  // Supabase queries), not this domain. Left as not-yet-implemented here.

  async getProfileAnalytics(_userId: string, _timeFilter: string = 'week'): Promise<ProfileAnalytics> {
    throw new Error('Use Dashboard/profileViewsService for profile analytics');
  }

  async exportAnalytics(_userId: string, _format: 'csv' | 'json' = 'csv'): Promise<Blob> {
    throw new Error('Use Dashboard/profileViewsService for profile analytics');
  }

  // Validation Methods — moderation triggers, belong to the Safety domain
  // (not yet built). Left as not-yet-implemented here.

  async validatePhotos(_userId: string, _photos: ProfilePhoto[]): Promise<any[]> {
    throw new Error('Photo validation triggers are not yet implemented (Safety domain)');
  }

  async validateContent(_userId: string, _content: { bio: Bio; interests: Interest[]; basicInfo: BasicInfo }): Promise<any> {
    throw new Error('Content validation triggers are not yet implemented (Safety domain)');
  }

  // Mock Methods for Development

  async mockCreateProfile(profileData: ProfileSetupData): Promise<ProfileSetupData> {
    await new Promise(resolve => setTimeout(resolve, 1000));
    return { ...profileData, isComplete: true, verificationStatus: 'verified' };
  }

  async mockGetProfile(_userId: string): Promise<ProfileEditorData> {
    await new Promise(resolve => setTimeout(resolve, 800));
    return {
      basicInfo: {
        firstName: 'John',
        lastName: 'Doe',
        dateOfBirth: new Date('1990-01-01'),
        gender: 'other',
        location: { city: 'New York', state: 'NY', country: 'US', coordinates: { lat: 40.7128, lng: -74.0060 } },
        occupation: 'Software Engineer',
        education: 'Bachelor\'s Degree'
      },
      photos: [
        { id: '1', url: '/api/placeholder/300/400', order: 0, isMain: true, uploadedAt: new Date(), isValidated: true, validationStatus: 'approved' }
      ],
      bio: { content: 'I love exploring new places and meeting interesting people!', characterCount: 65, maxCharacters: 500, lastUpdated: new Date() },
      interests: [
        { id: '1', name: 'Photography', category: 'hobbies', icon: '📸' },
        { id: '2', name: 'Travel', category: 'travel', icon: '✈️' }
      ],
      preferences: { ageRange: { min: 25, max: 35 }, distance: 20, genderPreference: ['female'], interestedIn: ['dating', 'serious'], dealBreakers: [] },
      lastUpdated: new Date(),
      version: 1
    };
  }

  async mockGetAnalytics(_userId: string): Promise<ProfileAnalytics> {
    await new Promise(resolve => setTimeout(resolve, 1200));
    return {
      totalViews: 1247, viewsToday: 23, viewsThisWeek: 156, viewsThisMonth: 892, uniqueViewers: 892,
      profileCompletion: 87,
      photoViews: { 'photo1': 892, 'photo2': 756 },
      bioViews: 1156,
      interestViews: { 'photography': 445, 'travel': 398 },
      matchRate: 0.23, responseRate: 0.67, lastUpdated: new Date()
    };
  }
}

export const profileService = new ProfileService();
